/**
 * useStartChildSession — the parent starts a session for one of her own children.
 *
 * first-run-guide (T5). Powers step 2 of FirstRunGuideCard: one tap, the session is created
 * in the child's name, and the parent lands in it while still signed in as herself. There is
 * no sign-out, no password, and no account switch anywhere in this flow.
 *
 * DELIBERATELY SEPARATE from useStartSession (the student's own "start" button) for one
 * reason that matters: useStartSession enforces CCM-03, which SUPPRESSES the out-of-coins
 * message so a child is never sold to. That rule is right for a child and wrong for a parent.
 * A parent is the person who can actually fix an empty wallet, so telling her plainly is the
 * correct behaviour, not a leak.
 *
 * In practice she should rarely see it: FirstRunGuideCard already knows the balance before
 * she taps and offers a top-up instead of a start it knows will fail. This hook covers the
 * race where the balance changed underneath her.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { startChildSession } from '@/services/sessions';
import type { ServerlessSessionResponse } from '@/types/session';
import type { AuthError } from '@/types/auth';

export interface UseStartChildSessionReturn {
  /** Start a session for the given child (must be one of the caller's own children). */
  startForChild: (studentId: string) => void;
  /** True while the session is being created. */
  isStarting: boolean;
  /** Parent-appropriate message, or null. Never swallowed (TO-LL-036). */
  error: string | null;
  /** Clear any error before a retry. */
  reset: () => void;
}

/**
 * The child already has a session open. Not an error the parent needs to see as one — the
 * guide card shows a "go to her session" action in this state instead.
 */
const CONFLICT_CODES = new Set([
  'ACTIVE_SESSION_EXISTS',
  'SESSION_ALREADY_ACTIVE',
  'SESSION_LIMIT_REACHED',
  'CONFLICT',
  'RATE_LIMITED',
]);

function messageFor(error: AuthError): string | null {
  const code = error?.code;

  if (code && CONFLICT_CODES.has(code)) {
    // Handled by refetching — the card flips to the walk-in action on the next render.
    return null;
  }
  if (code === 'INSUFFICIENT_COINS') {
    return 'You have no sessions left. Add some from the Plan and Coins tab and your child can begin straight away.';
  }
  if (code === 'FORBIDDEN' || code === 'UNAUTHORIZED') {
    return 'Please sign in again to start a session.';
  }
  return error?.message || 'Could not start the session. Please try again.';
}

export function useStartChildSession(): UseStartChildSessionReturn {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mutation = useMutation<ServerlessSessionResponse, AuthError, string>({
    mutationFn: (studentId: string) => startChildSession(studentId),
    onSuccess: (data) => {
      // The dashboard's live-session card and the guide's step 2 both key off this.
      queryClient.invalidateQueries({ queryKey: ['parent', 'dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['parent', 'sessions'] });
      if (data?.sessionId) {
        // TO-LL-013: internal validated path only, never a server-supplied URL.
        navigate(`/session/${data.sessionId}`);
      }
    },
    onError: (error) => {
      // On the conflict family the child already has a session running; refresh so the card
      // can offer the way into it rather than a second start.
      if (error?.code && CONFLICT_CODES.has(error.code)) {
        queryClient.invalidateQueries({ queryKey: ['parent', 'dashboard'] });
      }
    },
  });

  return {
    startForChild: (studentId: string) => mutation.mutate(studentId),
    isStarting: mutation.isPending,
    error: mutation.error ? messageFor(mutation.error) : null,
    reset: () => mutation.reset(),
  };
}
